import { hasAccess, type AccessRequest } from "@woven/contracts";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Clock3, MessageSquare, Tag } from "lucide-react";

import { useAuth, type AuthSession } from "../auth/context";
import { authErrorMessage } from "../auth/errors";
import { useControlApiClient } from "../lib/hooks";
import { BrandMark } from "./ui/BrandMark";
import { GithubMark, GoogleMark } from "./icons";
import { SignupAccountStatus } from "./SignupAccountStatus";
import { useSignupMembership } from "./useSignupMembership";

export type Interest = NonNullable<AccessRequest["interest"]>;

export interface FoundersClubModalProps {
  onClose: () => void;
  initialInterest?: Interest;
}

export function FoundersClubModal(props: FoundersClubModalProps) {
  return <FoundersClubSignup {...props} />;
}

export function FoundersClubSignup({
  onClose,
  initialInterest = "lite",
}: {
  onClose?: () => void;
  initialInterest?: Interest;
}) {
  const {
    user,
    session,
    isCurrentSession,
    loading,
    error,
    access,
    accessRequested,
    signInWithGoogle,
    signInWithGithub,
    signInWithEmail,
    createAccountWithEmail,
  } = useAuth();
  const client = useControlApiClient();
  const [posting, setPosting] = useState(false);
  const { error: membershipError, refresh: refreshAccountStatus } = useSignupMembership(posting);
  const [receipt, setReceipt] = useState<{ session: AuthSession; created: boolean } | null>(null);
  const [emailMode, setEmailMode] = useState<"signup" | "signin">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [interest, setInterest] = useState<Interest>(initialInterest);
  const [building, setBuilding] = useState("");
  const [authenticating, setAuthenticating] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<{
    input: AccessRequest;
    session: AuthSession;
  } | null>(null);
  const active = useRef(false);
  const busy = useRef(false);
  const attempt = useRef(0);
  const authenticatingSession = useRef<AuthSession | null>(null);
  const request = useRef<AbortController | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const submitting = authenticating || pendingRequest !== null;
  const disabled = submitting || loading;
  const uid = user?.uid;

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      attempt.current += 1;
      request.current?.abort();
      request.current = null;
      busy.current = false;
    };
  }, []);

  useEffect(() => {
    if (!authenticating) return;
    const id = attempt.current;
    void Promise.resolve().then(() => {
      if (!active.current || id !== attempt.current) return;
      const owner = authenticatingSession.current;
      if (owner && !isCurrentSession(owner)) {
        attempt.current += 1;
        busy.current = false;
        authenticatingSession.current = null;
        setAuthenticating(false);
        setEmail("");
        setPassword("");
        setBuilding("");
        setInterest(initialInterest);
        setReceipt(null);
        setLocalError(
          "The signed-in account changed. Your request was cancelled. Please submit it again."
        );
      } else if (session) authenticatingSession.current = session;
    });
  }, [authenticating, session, isCurrentSession, initialInterest]);

  useEffect(() => {
    if (!pendingRequest) return;
    const id = attempt.current;
    void Promise.resolve().then(() => {
      if (!active.current || id !== attempt.current) return;
      if (!isCurrentSession(pendingRequest.session)) {
        request.current?.abort();
        request.current = null;
        attempt.current += 1;
        busy.current = false;
        setPendingRequest(null);
        setPosting(false);
        setEmail("");
        setPassword("");
        setBuilding("");
        setInterest(initialInterest);
        setReceipt(null);
        setLocalError(
          "The signed-in account changed. Your request was cancelled. Please submit it again."
        );
        return;
      }
      if (
        request.current ||
        loading ||
        uid !== pendingRequest.session.uid ||
        error ||
        membershipError ||
        accessRequested === null
      )
        return;
      if (
        accessRequested ||
        hasAccess(access, "lite") ||
        (receipt && isCurrentSession(receipt.session))
      ) {
        busy.current = false;
        setPendingRequest(null);
        return;
      }
      const controller = new AbortController();
      request.current = controller;
      setPosting(true);
      const isActive = () =>
        active.current &&
        id === attempt.current &&
        !controller.signal.aborted &&
        isCurrentSession(pendingRequest.session);
      void client.requestAccess(pendingRequest.input, controller.signal).then(
        (result) => {
          if (!isActive()) return;
          setReceipt({ session: pendingRequest.session, created: result.created });
          request.current = null;
          busy.current = false;
          setPendingRequest(null);
          setPosting(false);
          refreshAccountStatus();
        },
        (cause: unknown) => {
          if (!isActive()) return;
          request.current = null;
          busy.current = false;
          setPosting(false);
          setLocalError(
            authErrorMessage(cause, "Your request could not be saved. Please try again.")
          );
          setPendingRequest(null);
        }
      );
    });
  }, [
    pendingRequest,
    session,
    uid,
    loading,
    error,
    access,
    accessRequested,
    receipt,
    client,
    isCurrentSession,
    membershipError,
    refreshAccountStatus,
    initialInterest,
  ]);

  useEffect(() => {
    if (!onClose) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && !submitting) onClose?.();
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, submitting]);

  function requestInput(): AccessRequest {
    const description = building.trim();
    return { interest, ...(description ? { building: description } : {}) };
  }

  async function authenticateAndRequest(action: () => Promise<AuthSession>, fallback: string) {
    if (busy.current || loading) return;
    busy.current = true;
    const id = ++attempt.current;
    authenticatingSession.current = null;
    const input = requestInput();
    setAuthenticating(true);
    setLocalError(null);

    try {
      const authenticated = await action();
      if (!active.current || id !== attempt.current) return;
      if (!isCurrentSession(authenticated)) {
        throw new Error(
          "The signed-in account changed. Your request was cancelled. Please submit it again."
        );
      }
      authenticatingSession.current = null;
      setPendingRequest({ input, session: authenticated });
    } catch (cause) {
      if (!active.current || id !== attempt.current) return;
      busy.current = false;
      const code = cause instanceof Error ? (cause as Error & { code?: string }).code : undefined;
      if (code === "auth/email-already-in-use") {
        setEmailMode("signin");
        setLocalError(
          "An account with that email already exists. Sign in to submit your access request."
        );
      } else if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request") {
        setLocalError(authErrorMessage(cause, fallback));
      }
    } finally {
      if (active.current && id === attempt.current) setAuthenticating(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (disabled || busy.current) return;
    setLocalError(null);

    if (user) {
      if (
        error ||
        membershipError ||
        accessRequested !== false ||
        access !== "guest" ||
        (receipt && isCurrentSession(receipt.session))
      )
        return;
      if (!session || !isCurrentSession(session)) {
        setLocalError("The signed-in account changed. Please submit your request again.");
        return;
      }
      busy.current = true;
      attempt.current += 1;
      setPendingRequest({ input: requestInput(), session });
      return;
    }

    if (!email.trim() || !password) return;
    void authenticateAndRequest(
      () =>
        emailMode === "signup"
          ? createAccountWithEmail(email.trim(), password)
          : signInWithEmail(email.trim(), password),
      emailMode === "signup"
        ? "Account creation failed. Please try again."
        : "Email sign-in failed. Please try again."
    );
  }

  const currentReceipt =
    receipt &&
    session &&
    receipt.session.uid === session.uid &&
    receipt.session.generation === session.generation
      ? receipt
      : null;
  const status =
    error || membershipError
      ? "error"
      : loading || (user && (accessRequested === null || !session || session.uid !== user.uid))
        ? "loading"
        : user && hasAccess(access, "lite")
          ? "approved"
          : user && accessRequested
            ? "received"
            : user && currentReceipt
              ? "loading"
              : null;
  if (status) {
    const statusContent = (
      <SignupAccountStatus
        status={status}
        email={user?.email}
        created={currentReceipt?.created}
        onRetry={user ? refreshAccountStatus : undefined}
        onClose={onClose}
      />
    );
    return onClose ? (
      <div className="modal-backdrop founders-backdrop" onClick={submitting ? undefined : onClose}>
        <div
          className="signup-status-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Access request status"
          onClick={(event) => event.stopPropagation()}
        >
          {statusContent}
        </div>
      </div>
    ) : (
      statusContent
    );
  }

  const Heading = onClose ? "h2" : "h1";
  const content = (
    <section
      className={onClose ? "founders-modal" : "founders-modal founders-signup"}
      role={onClose ? "dialog" : undefined}
      aria-modal={onClose ? true : undefined}
      aria-labelledby="founders-club-title"
      onClick={(event) => event.stopPropagation()}
    >
      <aside className="founders-intro">
        <div className="founders-intro-top">
          <BrandMark size={46} />
          <span className="founders-eyebrow">Early access</span>
        </div>
        <Heading id="founders-club-title">Become a Founder</Heading>
        <p>Request early access and help shape the Woven network. Access opens by approval only.</p>

        <ul className="founders-benefits">
          <li>
            <Tag size={20} strokeWidth={1.5} aria-hidden="true" />
            <span>Receive founder discount & status</span>
          </li>
          <li>
            <Clock3 size={20} strokeWidth={1.5} aria-hidden="true" />
            <span>Early access to Pro and Dedicated</span>
          </li>
          <li>
            <MessageSquare size={20} strokeWidth={1.5} aria-hidden="true" />
            <span>Direct feedback channel</span>
          </li>
        </ul>

        <p className="founders-no-payment">No payment required.</p>
      </aside>

      <div className="founders-form-wrap">
        {user ? (
          <p className="form-note">
            Signed in as {user.email ?? "your Woven account"}. Submit your access request below.
          </p>
        ) : (
          <>
            <div className="founders-provider-options">
              <button
                type="button"
                className="founders-google"
                onClick={() =>
                  void authenticateAndRequest(signInWithGoogle, "Google sign-in failed.")
                }
                disabled={disabled}
              >
                <GoogleMark size={21} />
                Continue with Google
              </button>
              <button
                type="button"
                className="founders-github"
                onClick={() =>
                  void authenticateAndRequest(signInWithGithub, "GitHub sign-in failed.")
                }
                disabled={disabled}
              >
                <GithubMark size={19} />
                Continue with GitHub
              </button>
            </div>

            <div className="founders-divider" role="separator">
              or join with email
            </div>
          </>
        )}

        <form className="founders-form" onSubmit={handleSubmit}>
          {!user ? (
            <>
              <label className="founders-label" htmlFor="founders-email">
                Email address
              </label>
              <input
                id="founders-email"
                className="input founders-input"
                type="email"
                name="email"
                autoComplete="email"
                placeholder="you@company.com"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={disabled}
              />

              <label className="founders-label" htmlFor="founders-password">
                {emailMode === "signup" ? "Create a password" : "Password"}
              </label>
              <input
                id="founders-password"
                className="input founders-input"
                type="password"
                name="password"
                autoComplete={emailMode === "signup" ? "new-password" : "current-password"}
                minLength={6}
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={disabled}
              />
            </>
          ) : null}

          <fieldset className="founders-interest" disabled={disabled}>
            <legend>I'm interested in</legend>
            <div className="founders-interest-options">
              {(["lite", "pro", "dedicated"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  className={
                    interest === option
                      ? "founders-interest-option founders-interest-option--active"
                      : "founders-interest-option"
                  }
                  aria-pressed={interest === option}
                  onClick={() => setInterest(option)}
                >
                  {option === "lite" ? "Lite" : option === "pro" ? "Pro" : "Dedicated"}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="founders-label" htmlFor="founders-building">
            What are you building? <span>Optional</span>
          </label>
          <input
            id="founders-building"
            className="input founders-input"
            type="text"
            name="building"
            placeholder="Game, app, simulation…"
            maxLength={1000}
            value={building}
            onChange={(event) => setBuilding(event.target.value)}
            disabled={disabled}
          />

          {localError ? (
            <p className="form-error" role="alert">
              {localError}
            </p>
          ) : null}

          <button
            type="submit"
            className="btn btn--primary btn--block founders-submit"
            disabled={disabled}
          >
            {submitting
              ? "Submitting request…"
              : loading
                ? "Checking session…"
                : user
                  ? "Request access"
                  : emailMode === "signup"
                    ? "Create account & join waitlist"
                    : "Sign in & request access"}
          </button>
        </form>

        {!user ? (
          <p className="founders-signin">
            {emailMode === "signup" ? "Already have an account?" : "New to Woven?"}{" "}
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setEmailMode(emailMode === "signup" ? "signin" : "signup");
                setLocalError(null);
              }}
              disabled={disabled}
            >
              {emailMode === "signup" ? "Sign in" : "Create an account"}
            </button>
          </p>
        ) : null}
        <p className="founders-legal">
          Creating an account does not grant console access. All requests are subject to
          availability.
        </p>
      </div>
    </section>
  );

  return onClose ? (
    <div className="modal-backdrop founders-backdrop" onClick={submitting ? undefined : onClose}>
      {content}
    </div>
  ) : (
    content
  );
}
